import { IntlShape } from "react-intl";

import {
  CareProfileUseCaseEnum,
  ClientRoleRelationshipEnum,
  GetJourneyWithLovedOneQuery,
  GetMeQuery
} from "@typing/Generated";
import { IconName } from "@utils/iconUtils";
import { getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type MyClient = Extract<GetMeQuery["me"], { __typename: "Client" }>;
type MyExpert = Extract<GetMeQuery["me"], { __typename: "Expert" }>;

type MenuSection = {
  iconName: IconName;
  skipMobile?: boolean;
  text: string;
  to: string;
  unread?: boolean;
};

export const clientMenuProps: (
  intl: IntlShape,
  journeyId: string,
  myClient: MyClient | undefined,
  journey?: GetJourneyWithLovedOneQuery["journey"] | undefined
) => MenuSection[] = (intl, journeyId, myClient) => [
  {
    iconName: "house",
    text: intl.formatMessage({ id: "menus.client.home" }),
    to: journeyRoute({ journeyId, linkAfterJourney: "/home", myClient })
  },
  {
    iconName: "users",
    text: intl.formatMessage({ id: "menus.client.community" }),
    to: journeyRoute({ journeyId, linkAfterJourney: "/community", myClient })
  },
  {
    iconName: "heart",
    text: intl.formatMessage({ id: "menus.client.care" }),
    to: journeyRoute({ journeyId, linkAfterJourney: "/care-plan", myClient })
  },
  {
    iconName: "store",
    text: intl.formatMessage({ id: "menus.client.marketplace" }),
    to: journeyRoute({ journeyId, linkAfterJourney: "/marketplace", myClient })
  },
  {
    iconName: "bookOpen",
    text: intl.formatMessage({ id: "menus.client.library" }),
    to: journeyRoute({ journeyId, linkAfterJourney: "/explore", myClient })
  }
];

export const expertJourneyMenuProps: (
  intl: IntlShape,
  journeyId: string,
  myExpert: MyExpert | undefined,
  journey?: GetJourneyWithLovedOneQuery["journey"] | undefined,
  isMobile?: boolean
) => MenuSection[] = (intl, journeyId, myExpert, journey, isMobile = false) => {
  const props: MenuSection[] = [
    {
      iconName: "house",
      text: isMobile
        ? intl.formatMessage({ id: "menus.expertJourney.plan" })
        : intl.formatMessage({ id: "menus.expertJourney.carePlan" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/care-plan", myExpert })
    },
    {
      iconName: "user",
      skipMobile: true,
      text: isMobile
        ? intl.formatMessage({ id: "menus.expertJourney.lovedOnesProfile" })
        : journey?.careProfile.useCase === CareProfileUseCaseEnum.SELF ||
            journey?.ownerRole?.relationship?.relationshipOption === ClientRoleRelationshipEnum.MYSELF
          ? intl.formatMessage({ id: "menus.expertJourney.profile" }, { name: journey.ownerRole?.client?.firstName })
          : intl.formatMessage({ id: "menus.expertJourney.profile" }, { name: getCareProfileName({ intl, journey }) }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/care-profile", myExpert })
    },
    {
      iconName: "calendar",
      text: intl.formatMessage({ id: "menus.expertJourney.sessions" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/expert", myExpert })
    },
    {
      iconName: "clock",
      text: intl.formatMessage({ id: "menus.expertJourney.timeLogs" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/time-logs", myExpert })
    },
    {
      iconName: "history",
      skipMobile: true,
      text: intl.formatMessage({ id: "menus.expertJourney.activity" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/activity", myExpert })
    },
    {
      iconName: "store",
      text: intl.formatMessage({ id: "menus.client.marketplace" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/marketplace", myExpert })
    },
    {
      iconName: "bookOpen",
      text: intl.formatMessage({ id: "menus.client.library" }),
      to: journeyRoute({ journeyId, linkAfterJourney: "/explore", myExpert })
    }
  ];

  return props;
};
